import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  Menu,
  Paper,
  Popper,
  useMediaQuery,
} from "@mui/material";
import { MuiBox, MuiTypography, SearchInput } from "components/atoms";
import { useTranslation } from "react-i18next";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

//SVGs

import DownArrow from "assets/images/svg-icons/chevron-down.svg";
import Search from "assets/images/svg-icons/Search.svg";
import { PeopleSuggestionAction } from "store/middlewares/general";
import { useDispatch, useSelector } from "react-redux";
import SmartImage from "components/atoms/SmartImage";

const PeopleMenu = ({ sortModal, setSortModal }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const closeTimerRef = useRef(null);
  const open = Boolean(anchorEl);
  const { peopleSuggestion, peopleSuggestionLoading } = useSelector(
    (state) => state.general,
  );
  const [filterName, setFilterName] = useState("");
  const [subMenuAnchor, setSubMenuAnchor] = useState(null);
  const [hoveredPerson, setHoveredPerson] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState({
    people: sortModal?.people || "",
    filterType: sortModal?.filterType || "all",
  });
  const isMobile = useMediaQuery("(max-width:600px)");

  const handleSubMenuOpen = (event, person) => {
    clearTimeout(closeTimerRef.current);
    setSubMenuAnchor(event.currentTarget);
    setHoveredPerson(person);
  };

  const handleSubMenuClose = () => {
    closeTimerRef.current = setTimeout(() => {
      setSubMenuAnchor(null);
      setHoveredPerson(null);
    }, 100);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const cancelSubMenuClose = () => {
    clearTimeout(closeTimerRef.current);
  };

  useEffect(() => {
    if (open) {
      dispatch(PeopleSuggestionAction());
    }
  }, [dispatch, open]);

  const filterOptions = [
    { label: "common.all", value: "all" },
    { label: "modal.filter.owner", value: "owner" },
    { label: "common.sharedWith", value: "shared" },
  ];

  return (
    <>
      {/* <CustomTooltip title={"Coming Soon!"}> */}
      <Button
        endIcon={
          <MuiBox sx={{ height: "20px", width: "20px", display: "flex" }}>
            <img src={DownArrow} alt="" className="img-full" />
          </MuiBox>
        }
        sx={{
          p: "8px 16px",
          color: "text.darkBlack",
          backgroundColor: "background.blue200",
          borderRadius: "8px",
          minWidth: "110px",
          width: "fit-content",
          flexShrink: 0,
          justifyContent: "space-between",
        }}
        onClick={handleClick}
      >
        {t("common.people")}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              maxWidth: "400px",
              mt: "8px",
              p: "16px",
              boxShadow: "0px 6px 24px 0px #0000000D",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "background.gray400",
            },
          },
        }}
      >
        <MuiBox sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <SearchInput
            sx={{ height: "45px" }}
            placeholder={t("commonButton.search")}
            // placeholder="Search here..."
            src={Search}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
          {peopleSuggestionLoading ? (
            <MuiBox
              sx={{
                // py: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={28} />
            </MuiBox>
          ) : peopleSuggestion?.filter((item) => {
              const fullName =
                (item?.first_name ?? "") + " " + (item?.last_name ?? "");

              return (
                fullName.toLowerCase().includes(filterName.toLowerCase()) ||
                item?.email?.toLowerCase().includes(filterName.toLowerCase())
              );
            }).length === 0 ? (
            <MuiTypography
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "text.gray900",
                mt: "10px",
              }}
            >
              {t("common.noUserFound")}
            </MuiTypography>
          ) : (
            peopleSuggestion
              ?.filter((item) => {
                const fullName =
                  (item?.first_name ?? "") + " " + (item?.last_name ?? "");

                return (
                  fullName.toLowerCase().includes(filterName.toLowerCase()) ||
                  item.email.toLowerCase().includes(filterName.toLowerCase())
                );
              })
              ?.map((person, index) => (
                <>
                  <MuiBox
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <MuiBox
                      onMouseEnter={(e) => handleSubMenuOpen(e, person)}
                      onMouseLeave={handleSubMenuClose}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        transition: "background-color 0.2s ease",
                        p: "8px",
                        borderRadius: "6px",
                        backgroundColor:
                          sortModal?.people === person?.email
                            ? "background.blue200"
                            : "background.white",
                        "&:hover": {
                          backgroundColor:
                            sortModal?.people === person?.email
                              ? "background.blue200"
                              : "background.gray200",
                        },
                      }}
                    >
                      <MuiBox
                        sx={{
                          height: "44px",
                          width: "44px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        <SmartImage
                          src={
                            person?.resized_image_urls?.medium ||
                            person?.image_url
                          }
                          alt={"profile"}
                          className="img-cover"
                          fallback={
                            <MuiBox
                              sx={{
                                bgcolor: person?.avatar?.backgroundColor,
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <MuiTypography variant="14" color="text.white">
                                {person?.avatar?.initials}
                              </MuiTypography>
                            </MuiBox>
                          }
                        />
                      </MuiBox>
                      <MuiBox
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <MuiTypography
                          fontWeight="500"
                          variant="14"
                          color="text.darkgray"
                        >
                          {person.first_name + " " + person.last_name}
                        </MuiTypography>
                        <MuiTypography
                          fontWeight="400"
                          variant="14"
                          color="text.gray800"
                        >
                          {person.email}
                        </MuiTypography>
                      </MuiBox>
                      <MuiBox
                        sx={{
                          width: "24px",
                          height: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <KeyboardArrowRightIcon fontSize="small" />
                      </MuiBox>
                    </MuiBox>
                    {index !== peopleSuggestion.length - 1 && <Divider />}
                  </MuiBox>
                  {/* <MuiBox
                  key={index}
                  sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
                >
                  <MuiBox
                    onMouseEnter={(e) => handleSubMenuOpen(e, person)}
                    onMouseLeave={handleSubMenuClose}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      transition: "background-color 0.2s ease",
                      p: "8px",
                      borderRadius: "6px",
                      backgroundColor:
                        sortModal?.people === person?.email
                          ? "background.blue200"
                          : "background.white",
                      "&:hover": {
                        backgroundColor:
                          sortModal?.people === person?.email
                            ? "background.blue200"
                            : "background.gray200",
                      },
                    }}
                  >
                    <Avatar
                      src={person.image_url}
                      sx={{
                        height: "44px",
                        width: "44px",
                        borderRadius: "6px",
                        color: "primary.main",
                        fontSize: "14px",
                        fontWeight: "500",
                        backgroundColor: "background.blue200",
                      }}
                    >
                      {person.avatar}
                    </Avatar>
                    <MuiBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <MuiTypography
                        fontWeight="500"
                        variant="14"
                        color="text.darkgray"
                      >
                        {person.first_name + " " + person.last_name}
                      </MuiTypography>
                      <MuiTypography
                        fontWeight="400"
                        variant="14"
                        color="text.gray800"
                      >
                        {person.email}
                      </MuiTypography>
                    </MuiBox>
                    <MuiBox
                      sx={{
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <KeyboardArrowRightIcon fontSize="small" />
                    </MuiBox>
                  </MuiBox>
                  {index !== peopleSuggestion.length - 1 && <Divider />}
                </MuiBox> */}
                </>
              ))
          )}
        </MuiBox>
      </Menu>

      <Popper
        open={Boolean(subMenuAnchor)}
        anchorEl={subMenuAnchor}
        placement={isMobile ? "bottom" : "right-start"}
        sx={{ zIndex: 1400 }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <Paper
          onMouseEnter={cancelSubMenuClose}
          onMouseLeave={handleSubMenuClose}
          sx={{
            width: "180px",
            p: "12px",
            boxShadow: "0px 6px 24px 0px #0000000D",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "background.gray400",
          }}
        >
          <MuiBox sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {filterOptions.map((option) => {
              const isSelected =
                selectedFilter.people === hoveredPerson?.email &&
                selectedFilter.filterType === option.value;

              return (
                <MuiBox
                  key={option.value}
                  onClick={() => {
                    setSelectedFilter({
                      people: hoveredPerson.email,
                      filterType: option.value,
                    });

                    setSortModal((prev) => ({
                      ...prev,
                      people: hoveredPerson.email,
                      filterType: option.value,
                    }));

                    setSubMenuAnchor(null);
                    setHoveredPerson(null);
                    handleClose();
                  }}
                  sx={{
                    px: "12px",
                    py: "5px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    bgcolor: isSelected ? "background.blue200" : "transparent",
                    color: isSelected ? "primary.main" : "text.darkBlack",
                    "&:hover": {
                      bgcolor: "background.gray200",
                    },
                  }}
                >
                  <MuiTypography
                    variant="14"
                    fontWeight={isSelected ? "600" : "400"}
                    color="inherit"
                  >
                    {t(option.label)}
                  </MuiTypography>
                </MuiBox>
              );
            })}
          </MuiBox>
        </Paper>
      </Popper>
      {/* <Menu
        anchorEl={subMenuAnchor}
        open={Boolean(subMenuAnchor)}
        // onClose={handleSubMenuClose}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        onClose={() => {
          setSubMenuAnchor(null);
          setHoveredPerson(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        MenuListProps={{
          onMouseEnter: cancelSubMenuClose,
          onMouseLeave: handleSubMenuClose,
          sx: {
            p: 0,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              width: "180px",
              // mt: "4px",
              p: "12px",
              boxShadow: "0px 6px 24px 0px #0000000D",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "background.gray400",
            },
          },
        }}
      >
        <MuiBox sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {filterOptions.map((option) => {
            const isSelected =
              selectedFilter.people === hoveredPerson?.email &&
              selectedFilter.filterType === option.value;

            return (
              <MuiBox
                key={option.value}
                onClick={() => {
                  setSelectedFilter({
                    people: hoveredPerson.email,
                    filterType: option.value,
                  });
                  setSortModal((prev) => ({
                    ...prev,
                    people: hoveredPerson.email,
                    filterType: option.value,
                  }));
                  handleSubMenuClose();
                  handleClose();
                }}
                sx={{
                  px: "12px",
                  py: "5px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  bgcolor: isSelected ? "background.blue200" : "transparent",
                  color: isSelected ? "primary.main" : "text.darkBlack",
                  fontWeight: isSelected ? 600 : 400,
                  transition: "all 0.2s",

                  "&:hover": {
                    bgcolor: "background.gray200",
                  },
                }}
              >
                <MuiTypography
                  variant="14"
                  fontWeight={isSelected ? "600" : "400"}
                  color="inherit"
                >
                  {t(option.label)}
                </MuiTypography>
              </MuiBox>
            );
          })}
        </MuiBox>
      </Menu> */}
    </>
  );
};

export default PeopleMenu;
